'use client'

import { ReactNode } from 'react'
import Link from 'next/link'

export interface ProcessStep {
  number: number
  title: string
  description: string
  icon?: ReactNode
}

interface ProcessStepsProps {
  steps: ProcessStep[]
  title?: string
  subtitle?: string
  className?: string
  layout?: 'horizontal' | 'vertical'
}

export default function ProcessSteps({
  steps,
  title,
  subtitle,
  className = '',
  layout = 'horizontal'
}: ProcessStepsProps) {
  if (!steps.length) {
    return null
  }

  return (
    <section className={`py-20 relative overflow-hidden ${className}`}>
      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left Column - Badge, Text, and Button */}
          <div className="animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-xs font-medium tracking-wider uppercase text-blue-400 ring-1 ring-inset ring-blue-500/30 mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
              </svg>
              <span>HOW ESKERIUM MAKES A DIFFERENCE</span>
            </div>
            
            {subtitle && (
              <p className="text-xl text-white/80 leading-relaxed mb-6">
                {subtitle}
              </p>
            )}
            
            <p className="text-lg text-eskerium-gold font-medium mb-8">
              By offering Live Resin Products, our customers gain a <strong>competitive advantage</strong> with high-value, full-spectrum products that stand out in the market.
            </p>

            {/* Neon Outline Button */}
            <Link 
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 text-lg font-medium text-purple-400 border-2 border-purple-500/50 rounded-lg bg-transparent backdrop-blur-sm shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.5)] hover:border-purple-400 hover:text-purple-300 transition-all duration-300"
            >
              Connect with Us
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14m-7-7 7 7-7 7"/>
              </svg>
            </Link>
          </div>

          {/* Right Column - Process Steps */}
          <div className="relative">
            <div className="space-y-8">
              {steps.map((step, index) => (
                <div key={step.number} className="relative flex items-start" style={{ animationDelay: `${index * 0.1}s` }}>
                  {/* Connecting Line (vertical) */}
                  {index < steps.length - 1 && (
                    <div className="absolute left-10 top-20 w-0.5 h-full bg-gradient-to-b from-purple-500/30 to-transparent"></div>
                  )}
                  
                  {/* Step Number Circle with Glassmorphism */}
                  <div className="flex-shrink-0 relative mr-6">
                    <div className="w-20 h-20 bg-purple-500/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white text-2xl font-bold border border-purple-500/30 shadow-[0_0_20px_rgba(168,85,247,0.2)] hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:border-purple-500/50 transition-all duration-300 group">
                      <span className="group-hover:text-purple-300 transition-colors duration-300">
                        {step.number}
                      </span>
                    </div>
                    {/* Subtle glow ring */}
                    <div className="absolute inset-0 w-full h-full border border-purple-500/20 rounded-full animate-pulse"></div>
                  </div>

                  {/* Step Content - LEFT ALIGNED */}
                  <div className="flex-1 pt-1 animate-fade-in">
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-base text-white/70 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}